/**
 * Composant d'export de données
 * Permet d'exporter les commandes et statistiques en CSV
 */
import { useState } from 'react';
import { Button } from './ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger 
} from './ui/dropdown-menu';
import { Download, FileSpreadsheet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function ExportButton({ className = '' }) {
  const [loading, setLoading] = useState(null);

  const downloadFile = async (url, filename) => {
    try {
      const response = await axios.get(url, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      
      toast.success('Export réussi !');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Erreur lors de l\'export');
    }
  };

  const handleExport = async (type, period = null) => {
    setLoading(type);
    
    try {
      if (type === 'orders') {
        await downloadFile(
          `${API}/restaurant-dashboard/export/orders`,
          `commandes_${new Date().toISOString().slice(0, 10)}.csv`
        );
      } else if (type === 'analytics') {
        await downloadFile(
          `${API}/restaurant-dashboard/export/analytics?period=${period || 'month'}`,
          `statistiques_${period || 'month'}_${new Date().toISOString().slice(0, 10)}.csv`
        );
      }
    } finally {
      setLoading(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className={`gap-2 ${className}`}>
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          Exporter
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Exporter les données</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        <DropdownMenuItem 
          onClick={() => handleExport('orders')}
          disabled={loading}
        >
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          Toutes les commandes
        </DropdownMenuItem>
        
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-gray-500">Statistiques</DropdownMenuLabel>
        
        <DropdownMenuItem 
          onClick={() => handleExport('analytics', 'week')}
          disabled={loading}
        >
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          7 derniers jours
        </DropdownMenuItem>
        
        <DropdownMenuItem 
          onClick={() => handleExport('analytics', 'month')}
          disabled={loading}
        >
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          30 derniers jours
        </DropdownMenuItem>
        
        <DropdownMenuItem 
          onClick={() => handleExport('analytics', 'year')}
          disabled={loading}
        >
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          12 derniers mois
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ExportButton;
